import React, { createContext, useState, useCallback, useRef } from "react";
import { getCookie, setCookie, removeCookie } from "../utils/cookieUtils";

export const TourContext = createContext();

const COOKIE_KEYS = {
  mainChat: "tourShownMainChat",
  collectionManagement: "tourShownCollectionMgmt",
  userManagement: "tourShownUserMgmt",
};

const FORCE_TOUR = process.env.REACT_APP_FORCE_TOUR === "true";

export const TourProvider = ({ children }) => {
  const [activeTour, setActiveTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const callbacksRef = useRef({});

  const hasSeenTour = useCallback((tourName) => {
    if (FORCE_TOUR) return false;
    return getCookie(COOKIE_KEYS[tourName]) === "true";
  }, []);

  const markTourSeen = useCallback((tourName) => {
    setCookie(COOKIE_KEYS[tourName], "true");
  }, []);

  const startTour = useCallback(
    (tourName) => {
      if (!hasSeenTour(tourName)) {
        setActiveTour(tourName);
        setStepIndex(0);
        setIsTourRunning(true);
      }
    },
    [hasSeenTour]
  );

  const endTour = useCallback(() => {
    if (activeTour) {
      markTourSeen(activeTour);
    }
    setActiveTour(null);
    setStepIndex(0);
    setIsTourRunning(false);
  }, [activeTour, markTourSeen]);

  const goToStep = useCallback((index) => {
    setStepIndex(index);
  }, []);

  const registerCallback = useCallback((name, fn) => {
    callbacksRef.current[name] = fn;
  }, []);

  const invokeCallback = useCallback((name, ...args) => {
    if (callbacksRef.current[name]) {
      callbacksRef.current[name](...args);
    }
  }, []);

  const resetTour = useCallback((tourName) => {
    if (tourName) {
      removeCookie(COOKIE_KEYS[tourName]);
    } else {
      Object.values(COOKIE_KEYS).forEach((key) => removeCookie(key));
    }
  }, []);

  return (
    <TourContext.Provider
      value={{
        activeTour,
        stepIndex,
        isTourRunning,
        startTour,
        endTour,
        goToStep,
        hasSeenTour,
        resetTour,
        registerCallback,
        invokeCallback,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
